/** The builder pick as data (no DOM): shared by the scene chip, the Towers tab badge and the builder row (builderStrip.ts). */
import { BalanceData, PlayerProfile, UpgradeJob, UpgradeTarget } from "@highmarch/domain";

/**
 * The next upgrade a free builder could take: the cheapest affordable one among the base, the owned towers and (Pets
 * v2) the pets caught, else the cheapest overall. `target` says which: `towerIndex` is the tower's index, or the pet's
 * roster index for UpgradeTarget.Pet (the upgrade system's own key).
 */
export interface UpgradeOption { readonly target: UpgradeTarget; readonly towerIndex: number; readonly level: number; readonly cost: number; readonly affordable: boolean }

/**
 * What the builder is doing, as data (the scene chip, the Towers tab badge and the builder row all read the same pick,
 * so they can never disagree):
 * - `idle`: a builder is free; `option` is the suggested upgrade and `actionable` is true when it can start now.
 * - `busy`: no builder has anything it can do, and a job runs (the one ending soonest).
 * - `maxed`: nothing left to upgrade and nothing running.
 * A free builder with an affordable upgrade wins over a running job: that is the thing the player can act on.
 */
export type BuilderPick =
  | { readonly kind: "idle"; readonly option: UpgradeOption; readonly actionable: boolean }
  | { readonly kind: "busy"; readonly job: UpgradeJob; readonly target: UpgradeTarget; readonly towerIndex: number; readonly progress: number; readonly remaining: number }
  | { readonly kind: "maxed" };

const baseIndexOf = (B: BalanceData): number => B.towers.findIndex((t) => t.isBase);

/**
 * Cheapest next upgrade among the base, the owned towers and, with `pets`, the pets caught (under their level cap): the
 * cheapest the player can afford now, else the cheapest overall. A target already upgrading is skipped.
 */
export function cheapestUpgrade(B: BalanceData, p: PlayerProfile, owned: readonly boolean[], pets = false): UpgradeOption | null {
  let best: UpgradeOption | null = null, cheapest: UpgradeOption | null = null;
  const consider = (target: UpgradeTarget, i: number, level: number, cap: number): void => {
    if (level >= cap || p.isUpgrading(target, i)) return;
    const cost = p.costFor(level + 1), o: UpgradeOption = { target, towerIndex: i, level, cost, affordable: p.canAfford(cost) };
    if (cheapest === null || cost < cheapest.cost) cheapest = o;
    if (o.affordable && (best === null || cost < best.cost)) best = o;
  };
  B.towers.forEach((t, i) => {
    if (!t.isBase && !owned[i]) return;
    consider(t.isBase ? UpgradeTarget.Base : UpgradeTarget.Tower, i, t.isBase ? p.baseLevel : p.towerLevels[i], B.tables.maxLevel);
  });
  if (pets) B.pets.list.forEach((_, i) => { const level = p.levelOf(UpgradeTarget.Pet, i); if (level > 0) consider(UpgradeTarget.Pet, i, level, p.upgradeCap(UpgradeTarget.Pet, i)); });
  return best ?? cheapest;
}

/** The builder's state now (see `BuilderPick`). */
export function builderPick(B: BalanceData, p: PlayerProfile, owned: readonly boolean[], now: number): BuilderPick {
  const option = cheapestUpgrade(B, p, owned, true);
  const free = p.freeBuilders > 0;
  if (free && option?.affordable) return { kind: "idle", option, actionable: true };
  const job = [...p.jobs].sort((a, b) => a.endAt - b.endAt)[0];
  if (job) {
    const progress = Math.min(1, Math.max(0, (now - job.startedAt) / Math.max(1, job.endAt - job.startedAt)));
    return { kind: "busy", job, target: job.target, towerIndex: job.target === UpgradeTarget.Base ? baseIndexOf(B) : job.towerIndex, progress, remaining: Math.max(0, job.endAt - now) };
  }
  return option ? { kind: "idle", option, actionable: false } : { kind: "maxed" };
}

/** What the pick points at: the job's target, or the suggested upgrade's; null when everything is maxed. */
export function pickTarget(pick: BuilderPick): { readonly target: UpgradeTarget; readonly towerIndex: number } | null {
  return pick.kind === "busy" ? { target: pick.target, towerIndex: pick.towerIndex } : pick.kind === "idle" ? { target: pick.option.target, towerIndex: pick.option.towerIndex } : null;
}

/** The tower (or base) the pick points at; −1 when everything is maxed or it points at a pet (pickTarget says which). */
export function pickTowerIndex(pick: BuilderPick): number {
  const t = pickTarget(pick);
  return t === null || t.target === UpgradeTarget.Pet ? -1 : t.towerIndex;
}
